import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import { TextField as OriginalTextField } from '../TextField';

interface IProps<TFieldValues extends FieldValues = FieldValues>
  extends
    UseControllerProps<TFieldValues>,
    Omit<
      React.ComponentProps<typeof OriginalTextField>,
      'name' | 'value' | 'defaultValue' | 'onChange' | 'error' | 'required'
    > {
  isRequired?: boolean;
  transformValue?: (value: string) => string;
}

const TextField = <TFieldValues extends FieldValues>({
  description,
  name,
  rules,
  control,
  defaultValue,
  shouldUnregister,
  isRequired,
  transformValue = value => value,
  ...inputProps
}: IProps<TFieldValues>) => {
  const intl = useIntl();

  const {
    field: { ref, ...field },
    fieldState: { error },
  } = useController({
    name,
    rules: {
      required: isRequired
        ? {
            value: true,
            message: intl.formatMessage({
              defaultMessage: 'Field is required.',
              id: 'Q4ipsb',
              description: 'Required field validation message',
            }),
          }
        : undefined,
      ...rules,
    },
    control,
    defaultValue,
    shouldUnregister,
  });

  const handleChange = useCallback<
    NonNullable<React.ComponentProps<typeof OriginalTextField>['onChange']>
  >(
    (_e, { value }) => {
      field.onChange(transformValue(value));
    },
    [field, transformValue],
  );

  return (
    <OriginalTextField
      {...field}
      {...inputProps}
      onChange={handleChange}
      inputRef={ref}
      value={field.value as string} // to make TS happy, maybe we can tell TS only string values accepted here ?
      error={error?.message}
      description={!error?.message && description ? description : undefined}
    />
  );
};

export { TextField };
